/**
 * CompositeDataPicker
 *
 * Composite component that combines multiple data pickers
 * - Intelligent path routing
 * - Unified loading state management
 * - Single array input/output
 * - Invalid paths handling
 */

import React, { useMemo, useEffect } from 'react';
import { Box, Tabs, Tab, LinearProgress, Typography } from '@mui/material';
import { AudienceFieldSelector } from '../AudienceFieldSelector';
import { TaskFieldSelector } from '../TaskFieldSelector';
import { VariableFieldSelector } from '../VariableFieldSelector';
import { InsightVariableFieldSelector } from '../InsightVariableFieldSelector';
import { SocialProofFieldSelector } from '../SocialProofFieldSelector';
import { InsightsFieldSelector } from '../InsightsFieldSelector';
import { InvalidPathsPanel } from './InvalidPathsPanel';
import { useCompositeState } from './useCompositeState';
import type { CompositeDataPickerProps, HydratedData, PickerConfig } from './types';
import type { LoadingState } from '../../shared/types';

// Default picker configurations
const DEFAULT_PICKERS: PickerConfig[] = [
  { type: 'audience', label: 'Audiences', enabled: true },
  { type: 'task', label: 'Tasks', enabled: true },
];

/**
 * CompositeDataPicker Component
 */
export const CompositeDataPicker: React.FC<CompositeDataPickerProps> = ({
  projectId,
  savedSelections,
  onSelectionChange,
  onLoadingChange,
  onHydratedDataChange,
  pickers = DEFAULT_PICKERS,
  layout = 'tabs',
  height = 400,
  showInvalidPaths = true,
  variables,
  taskList,
  crossFilterByInsight = false,
  pinnedAnalysisTaskIds,
}) => {
  const [activeTab, setActiveTab] = React.useState(0);
  // Task 9: hydrated insight-derived variables are merged into the composite
  // HydratedData below; the selection itself lives in the reducer state.
  const [insightVariableHydrated, setInsightVariableHydrated] = React.useState<
    import('@segmently/schema-models/v2').FunnelVariable[]
  >([]);
  // Phase F: the task ids spanned by the selected InsightsAnalysis (emitted by InsightsFieldSelector).
  // UXR C5: when the analysis is picked OUTSIDE this composite (radio cards),
  // the caller supplies the span via pinnedAnalysisTaskIds instead.
  const [internalAnalysisTaskIds, setInternalAnalysisTaskIds] = React.useState<string[] | undefined>(undefined);
  const analysisTaskIds = pinnedAnalysisTaskIds ?? internalAnalysisTaskIds;
  const setAnalysisTaskIds = setInternalAnalysisTaskIds;
  const handlePinnedAnalysisChange = React.useCallback(
    (info: { analysisId: string | null; taskIds: string[] }) =>
      setAnalysisTaskIds(info.analysisId ? info.taskIds : undefined),
    [],
  );

  // Use composite state hook
  const {
    audiencePaths,
    taskPaths,
    variablePaths,
    insightVariablePaths,
    socialProofPaths,
    insightsPaths,
    invalidPaths,
    handleAudienceChange,
    handleTaskChange,
    handleVariableChange,
    handleInsightVariableChange,
    handleSocialProofChange,
    handleInsightsChange,
    handleAudienceLoadingChange,
    handleTaskLoadingChange,
    handleAudienceHydratedDataChange,
    handleTaskHydratedDataChange,
    handleVariableHydratedDataChange,
    handleSocialProofHydratedDataChange,
    handleInsightsHydratedDataChange,
  } = useCompositeState({
    savedSelections,
    onSelectionChange,
    onLoadingChange,
    // Task 9: the insight-derived variables are owned by THIS component (the
    // hook has no source for them), so they are folded into the aggregate the
    // hook emits rather than duplicating the aggregation.
    onHydratedDataChange: React.useMemo(
      () => (onHydratedDataChange
        ? (data: HydratedData) => onHydratedDataChange({
          ...data,
          insightVariables: insightVariableHydrated.length > 0 ? insightVariableHydrated : undefined,
        })
        : undefined),
      [onHydratedDataChange, insightVariableHydrated],
    ),
  });

  // Log invalid paths to console
  useEffect(() => {
    if (invalidPaths.length > 0) {
      console.warn('[CompositeDataPicker] Invalid paths detected:', invalidPaths);
    }
  }, [invalidPaths]);

  // Get enabled pickers
  const enabledPickers = useMemo(() => pickers.filter((p) => p.enabled), [pickers]);

  // Phase F: the currently-selected task ids (drives the Variables filter when cross-filtering
  // and, from Task 8, the follow-the-task-set rule). Deduplicated: one task contributes both
  // `@task[id]` and `@task[id].field` paths.
  const selectedTaskIds = useMemo(
    () => Array.from(new Set(
      taskPaths
        .map((p) => p.match(/^@task\[([^\]]+)\]/)?.[1])
        .filter((x): x is string => !!x),
    )),
    [taskPaths],
  );

  // Render picker content
  const renderPickerContent = (picker: PickerConfig) => {
    switch (picker.type) {
      case 'audience':
        return (
          <AudienceFieldSelector
            projectId={projectId}
            value={audiencePaths}
            onChange={handleAudienceChange}
            height={height}
            selectAll={picker.selectAll}
            onHydratedDataChange={handleAudienceHydratedDataChange}
            highlightEmptyAudiences={picker.highlightEmptyAudiences}
          />
        );

      case 'task':
        return (
          <TaskFieldSelector
            projectId={projectId}
            value={taskPaths}
            onChange={handleTaskChange}
            height={height}
            selectAll={picker.selectAll}
            onHydratedDataChange={handleTaskHydratedDataChange}
            allowedTaskIds={crossFilterByInsight ? analysisTaskIds : undefined}
          />
        );

      case 'variable':
        return variables ? (
          <VariableFieldSelector
            variables={variables}
            tasks={taskList}
            value={variablePaths}
            onChange={handleVariableChange}
            height={height}
            selectAll={picker.selectAll ?? true}
            onHydratedDataChange={handleVariableHydratedDataChange}
            // AC-016/017/018: declaring `followSelectedTasks` replaces the legacy
            // visibility filter — variables stay visible and follow the routes.
            taskIdFilter={
              crossFilterByInsight && picker.followSelectedTasks === undefined
                ? selectedTaskIds
                : undefined
            }
            followSelectedTasks={picker.followSelectedTasks}
            selectedTaskIds={selectedTaskIds}
            showSelectionSummary={picker.headerCaption === undefined}
          />
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No variables provided. Pass `variables` prop to CompositeDataPicker.
            </Typography>
          </Box>
        );

      case 'insightVariables':
        return (
          <InsightVariableFieldSelector
            projectId={projectId}
            generationId={picker.generationId}
            value={insightVariablePaths}
            onChange={handleInsightVariableChange}
            tasks={taskList}
            height={height}
            selectAll={picker.selectAll ?? true}
            onHydratedDataChange={setInsightVariableHydrated}
            emptyStateAction={picker.emptyStateAction}
            emptyStateMessage={picker.emptyStateMessage}
            showSelectionSummary={picker.headerCaption === undefined}
          />
        );

      case 'socialproof':
        return (
          <SocialProofFieldSelector
            projectId={projectId}
            value={socialProofPaths}
            onChange={handleSocialProofChange}
            height={height}
            selectAll={picker.selectAll}
            onHydratedDataChange={handleSocialProofHydratedDataChange}
          />
        );

      case 'insights':
        return (
          <InsightsFieldSelector
            projectId={projectId}
            value={insightsPaths}
            onChange={handleInsightsChange}
            height={height}
            selectAll={picker.selectAll}
            onHydratedDataChange={handleInsightsHydratedDataChange}
            onPinnedAnalysisChange={crossFilterByInsight ? handlePinnedAnalysisChange : undefined}
          />
        );

      default:
        return null;
    }
  };

  // Tabs layout
  if (layout === 'tabs') {
    return (
      <Box data-testid="composite-data-picker">
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} data-testid="composite-picker-tabs">
          {enabledPickers.map((picker, index) => {
            const count =
              picker.type === 'audience' ? audiencePaths.length
              : picker.type === 'task' ? taskPaths.length
              : picker.type === 'socialproof' ? socialProofPaths.length
              : picker.type === 'insights' ? insightsPaths.length
              : picker.type === 'insightVariables' ? insightVariablePaths.length
              : variablePaths.length;
            return (
              <Tab
                key={picker.type}
                label={`${picker.label} (${count})`}
                data-testid={`composite-picker-tab-${picker.type}`}
              />
            );
          })}
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {enabledPickers.map((picker, index) => (
            <Box
              key={picker.type}
              role="tabpanel"
              data-testid={`composite-picker-panel-${picker.type}`}
              sx={{ display: activeTab !== index ? 'none' : 'block', height: '100%' }}
            >
              {renderPickerContent(picker)}
            </Box>
          ))}
        </Box>

        {showInvalidPaths && <InvalidPathsPanel invalidPaths={invalidPaths} />}
      </Box>
    );
  }

  const pickerCount = (picker: PickerConfig): number =>
    picker.type === 'audience' ? audiencePaths.length
    : picker.type === 'task' ? taskPaths.length
    : picker.type === 'socialproof' ? socialProofPaths.length
    : picker.type === 'insights' ? insightsPaths.length
    : picker.type === 'insightVariables' ? insightVariablePaths.length
    : variablePaths.length;

  // Vertical (guided) layout — dependency-ordered numbered sections (Phase F: insight-first picker).
  if (layout === 'vertical') {
    return (
      <Box data-testid="composite-data-picker-vertical">
        {enabledPickers.map((picker, index) => (
          <Box
            key={picker.type}
            data-testid={picker.sectionTestId ?? `composite-picker-section-${picker.type}`}
            sx={{ py: 1.5, borderBottom: index < enabledPickers.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {index + 1}
              </Box>
              <Typography variant="subtitle2">{picker.label}</Typography>
              <Typography variant="caption" color="text.secondary">({pickerCount(picker)})</Typography>
              {picker.headerAction && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{picker.headerAction}</Box>
              )}
            </Box>
            {picker.headerCaption && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
                data-testid={`composite-picker-caption-${picker.type}`}
              >
                {picker.headerCaption}
              </Typography>
            )}
            {renderPickerContent(picker)}
          </Box>
        ))}
        {showInvalidPaths && <InvalidPathsPanel invalidPaths={invalidPaths} />}
      </Box>
    );
  }

  // Side-by-side layout
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {enabledPickers.map((picker) => (
          <Box key={picker.type} sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {picker.label} ({pickerCount(picker)})
            </Typography>
            {renderPickerContent(picker)}
          </Box>
        ))}
      </Box>

      {showInvalidPaths && <InvalidPathsPanel invalidPaths={invalidPaths} />}
    </Box>
  );
};
